import { computed, defineComponent, ref } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";
import moment from "moment";

import { useApp } from "../../../../App/useApp";
import { useUserStore } from "../../../store/useUsersStore";
import { useMessagesStore } from "../../../store/useMessagesStore";
import { useCallStore } from "../../../store/useCallStore";
import { useConversationStore } from "../../../store/useConversationStore";
import {
    IConversationUser,
    RoomDetails
} from "./../../../../../resources-vue/vue-interfaces/messaging-service/i-message";
import { jitsiRoomHandler } from "../../../../../utilities/jitsiRoomHandler";
import {
    ICallControls,
    ICallStatusChange,
    IOutgoingCallProps
} from "../../../../../resources-vue/vue-interfaces/messaging-service/i-notification";
import {
    ConversationRoomTypeEnum,
    jitsiConferenceStatus,
    AttendanceTypeEnum,
    NotificationsHubEvents,
    SignalRHubs,
    DraftConvEnum,
    ChatNavOptionsEnum,
    ConversationTypeEnum,
    AvaInitialsType
} from "../../../../../enums/enums";
import eventBus from "../../../../../utilities/eventBus";
import { useDraftsStore } from "../../../store/useDraftsStore";
import { useLoadingIndicator } from "../../../../Common/LoadingIndicator/store/useLoadingIndicator";
import { formatConversationTitle } from "../../../../../common/vue-helpers/modifiers/format-message";
import { MessageService } from "../../../../../services/message-service";
import { SignalRService } from "../../../../../services/signal-r-service";
import { IPromptOptions } from "../../../../../resources-vue/vue-interfaces/i-prompt";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";

import AddMemberToChat from "../../../../../resources-vue/vue-dialogs/AddMemberToChat/AddMemberToChat.vue";
import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import AvaInitials from "../../../components/AvaInitials/AvaInitials.vue";

export default defineComponent({
    components: {
        AvaInitials
    },
    props: {
        roomNameTemplate: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            _analyticsService: null as AnalyticsService,
            _messageService: null as MessageService,
            dialogRef: ref(null),
            visible: false as boolean,
            dialogOpened: false as boolean,
            conversationRoomDetails: null as RoomDetails,
            ConversationRoomTypeEnum: ConversationRoomTypeEnum,
            activeConversationType: ConversationRoomTypeEnum.AudioCall,
            signalRService: null as SignalRService,
            callStatus: null as ICallStatusChange,
            callDuration: null as string,
            axxessUserName: "Axxess",
            intervalId: null as NodeJS.Timeout,
            userOs: "" as string,
            callControls: null as ICallControls,
            disableCallBtns: false as boolean,
            ConversationTypeEnum,
            AvaInitialsType
        };
    },

    async mounted() {
        this.signalRService = Container.instance.get(SignalRService);
        this.callControls = {
            audioEnabled: true,
            videoEnabled: false
        };
        await this.setUpCallStatusChangeEvent();
    },

    async created() {
        this.userOs = window.sessionStorage.getItem("userOs");
        this._messageService = Container.instance.get(MessageService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        eventBus.on("dialogOpened", this.handleJitsiDialogOpened);
        eventBus.on("updateCallControls", this.handleCallControlsUpdate);
        eventBus.on("retryCall", this.handleRetryCall);
        eventBus.on("startJitiCall", this.handleStartJitsiCall);
        eventBus.on("updateDisableCallBtn", (response: boolean) => {
            this.disableCallBtns = response;
        });
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef.value)
        };
    },
    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),
        ...mapState(useConversationStore, { activeConversation: "getActiveConversation" }),
        ...mapState(useUserStore, ["getUser", "getMultipleUsers"]),
        ...mapState(useCallStore, ["currentCallDetails", "showCallHeader", "outgoingCallInfo"]),

        showJoinBtn() {
            return (
                this.showCallHeader.convId == this.activeConversation?.id &&
                this.showCallHeader.show &&
                this.currentCallDetails.convId !== this.activeConversation?.id
            );
        },
        isCallOngoing() {
            return this.showCallHeader.show && this.showCallHeader.convId === this.activeConversation?.id;
        },
        isGroupConversation() {
            return this.activeConversation.conversationType === ConversationTypeEnum.GroupConversation;
        }
    },

    watch: {
        "showCallHeader.callStartTime": {
            handler() {
                this.startCallTimer(this.showCallHeader.callStartTime);
            }
        }
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        ...mapActions(useCallStore, [
            "setCurrentCallDetails",
            "setShowCallHeader",
            "setOutgoingCallInfo",
            "markAttendanceForUser"
        ]),
        ...mapActions(useMessagesStore, ["fetchNewerMessages"]),
        ...mapActions(useConversationStore, ["updateConversationUsers"]),
        ...mapActions(useDraftsStore, ["updateDraftConversation"]),

        generateRoomName() {
            return `${this.roomNameTemplate}-${Date.now()}`;
        },
        handleJitsiDialogOpened(isJitsiOpen: boolean) {
            this.dialogOpened = isJitsiOpen;
            if (!isJitsiOpen) {
                this.disableCallBtns = false;
            }
        },

        async setUpCallStatusChangeEvent() {
            try {
                this.signalRService.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.CallStatusChange,
                    async (callStatus: ICallStatusChange) => {
                        this.callStatus = callStatus;
                        this.updateStatus(this.activeConversation.id);
                        if (
                            callStatus.conferenceStatus === jitsiConferenceStatus.Ended &&
                            this.currentCallDetails.convId === callStatus.conversationId
                        ) {
                            if (
                                this.dialogOpened &&
                                (this.$route?.query?.tab === ChatNavOptionsEnum.Chat ||
                                    this.$route?.query?.tab === ChatNavOptionsEnum.Call)
                            ) {
                                this.markAttendanceForUser(AttendanceTypeEnum.Leave);
                            }
                        } else if (
                            callStatus.conferenceStatus === jitsiConferenceStatus.Ended &&
                            this.activeConversation.id === callStatus.conversationId
                        ) {
                            this.fetchNewerMessages(this.activeConversation.id);
                        }
                    }
                );
            } catch (error) {
                console.error("Error running SignalR service:", error);
            }
        },

        async updateStatus(conversationId: string) {
            // update status when user gets a call while on current conv page
            if (conversationId === this.callStatus.conversationId) {
                if (this.callStatus.conferenceStatus === jitsiConferenceStatus.Ongoing) {
                    await this.getConversationRoomStatus();
                    this.setShowCallHeader({
                        convId: conversationId,
                        show: this.activeConversation.conversationType === ConversationTypeEnum.GroupConversation,
                        roomId: this.callStatus.roomId,
                        callStartTime: this.conversationRoomDetails.startTime
                    });
                    this.startCallTimer(this.showCallHeader.callStartTime);
                } else {
                    clearInterval(this.intervalId);
                    this.setShowCallHeader({
                        convId: null,
                        show: false,
                        roomId: null,
                        callStartTime: null
                    });
                }
            }
        },

        async joinConversationRoom() {
            const isUserOnCall = this.currentCallDetails.isUserOnCall;
            this.callControls = {
                audioEnabled: false,
                videoEnabled: false
            };
            if (isUserOnCall) {
                const promptOptions: IPromptOptions = {
                    message: "Joining this call will disconnect your active call",
                    okText: "Join",
                    cancelText: "Cancel"
                };
                this.dialogRef = this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        showHeader: false
                    },
                    data: { promptOptions },
                    onClose: async (confirm: any) => {
                        if (confirm.data) {
                            eventBus.emit("endCurrentCall");
                            this.setCurrentCallDetails({
                                convId: this.activeConversation.id,
                                roomId: this.showCallHeader.roomId,
                                isUserOnCall: true
                            });
                            await this.handleJoinRoom(this.showCallHeader.roomId, false);
                        }
                    }
                });
            } else {
                this.setCurrentCallDetails({
                    convId: this.activeConversation.id,
                    roomId: this.showCallHeader.roomId,
                    isUserOnCall: true
                });
                await this.handleJoinRoom(this.showCallHeader.roomId, false);
            }
        },

        async sendCallNotification(roomId: string, isRetryCall: boolean = false) {
            const callerName = `${this.loggedInUserDetails.firstName} ${this.loggedInUserDetails.lastName}`;
            let callMsg = `${callerName} is calling`;
            if (!isRetryCall) {
                if (this.activeConversation.conversationType === ConversationTypeEnum.GroupConversation) {
                    this.setShowCallHeader({
                        convId: this.activeConversation.id,
                        show: true,
                        roomId: roomId,
                        callStartTime: moment()
                    });
                }
                this.setCurrentCallDetails({
                    convId: this.activeConversation.id,
                    roomId,
                    isUserOnCall: true
                });
                callMsg =
                    this.activeConversation?.conversationType === ConversationTypeEnum.GroupConversation
                        ? this.activeConversation?.title?.length > 0
                            ? this.activeConversation.title
                            : formatConversationTitle(this.activeConversation?.loginIds, 100)
                        : `${callerName} is calling`;
            }

            const payload = {
                subjectId: this.loggedInUserDetails.loginId,
                callerName,
                callMessage: callMsg
            };
            await this._messageService.sendCallNotification(roomId, payload);

            if (this.outgoingCallInfo.convType == ConversationTypeEnum.GroupConversation) {
                await this.getConversationRoomStatus(false);
                await this.handleJoinRoom(roomId, false);
                await this.markAttendanceForUser(AttendanceTypeEnum.Join, this.activeConversation?.id, roomId);
                this.startCallTimer(this.conversationRoomDetails.startTime);
            } else {
                this.handleCalling(roomId, isRetryCall);
            }
        },

        handleCalling(roomId: string, isRetryCall: boolean = false) {
            let outgoingDetails: IOutgoingCallProps;
            if (isRetryCall) {
                outgoingDetails = {
                    recipient: this.outgoingCallInfo.users[0],
                    callType: this.outgoingCallInfo.roomType,
                    conversationId: this.outgoingCallInfo.convId,
                    roomId,
                    callControls: this.callControls
                };
                this.setOutgoingCallInfo({ ...this.outgoingCallInfo, isRetryCall: true });
            } else {
                outgoingDetails = {
                    recipient: this.getUser(this.activeConversation.loginIds[0]),
                    callType: this.activeConversationType,
                    conversationId: this.activeConversation.id,
                    roomId,
                    callControls: this.callControls
                };
            }
            eventBus.emit("outgoingCallNotification", outgoingDetails);
        },

        async handleStartJitsiCall(callDetails: any) {
            const { roomId, isRetryCall, isFamilyPortalCall } = callDetails;
            if (isFamilyPortalCall) {
                return;
            }

            await this.getConversationRoomStatus(isRetryCall);
            await this.handleJoinRoom(roomId, isRetryCall);
            await this.markAttendanceForUser(AttendanceTypeEnum.Join, this.activeConversation.id, roomId, isRetryCall);
            this.setCurrentCallDetails({
                convId: this.outgoingCallInfo.convId,
                roomId,
                isUserOnCall: true
            });
            this.startCallTimer(this.conversationRoomDetails.startTime);
            this.disableCallBtns = false;
            this.fetchNewerMessages(this.outgoingCallInfo.convId);
        },

        startCallTimer(time: string) {
            clearInterval(this.intervalId);
            if (time && this.showCallHeader.show) {
                const callDate = moment.utc(time).local();
                this.intervalId = setInterval(() => {
                    const difference = moment(moment()).diff(moment(callDate), "seconds");
                    const duration = moment.duration(difference, "seconds");
                    this.callDuration =
                        difference >= 3600
                            ? moment.utc(duration.asMilliseconds()).format("HH:mm:ss")
                            : moment.utc(duration.asMilliseconds()).format("mm:ss");
                }, 1000);
            }
        },

        async getConversationRoomStatus(isRetryCall: boolean = false) {
            try {
                const conversationRoomStatus = await this._messageService.getConversationRoomStatus(
                    isRetryCall ? this.outgoingCallInfo.convId : this.activeConversation.id
                );
                this.conversationRoomDetails = conversationRoomStatus.roomDetails;
                this.setShowCallHeader({
                    convId: this.showCallHeader.convId,
                    show: isRetryCall
                        ? false
                        : this.activeConversation?.conversationType === ConversationTypeEnum.GroupConversation,
                    roomId: this.showCallHeader.roomId,
                    callStartTime: this.conversationRoomDetails.startTime
                });
            } catch (error) {
                console.error("Error fetching conversation roomm status", error);
            }
        },

        async startMeeting(roomType: ConversationRoomTypeEnum, isRetryCall: boolean = false) {
            this.disableCallBtns = isRetryCall ? false : this.disableCallBtns;
            if (!this.disableCallBtns) {
                this.disableCallBtns = true;
                this.activeConversationType = roomType;
                if (this.activeConversationType == ConversationRoomTypeEnum.AudioCall) {
                    this.callControls = {
                        audioEnabled: true,
                        videoEnabled: false
                    };
                } else {
                    this.callControls = {
                        audioEnabled: true,
                        videoEnabled: true
                    };
                }
                if (!isRetryCall) {
                    this.setOutgoingCallInfo({
                        convId: this.activeConversation.id,
                        users: this.getMultipleUsers(this.activeConversation.loginIds),
                        convType: this.activeConversation.conversationType,
                        roomType: roomType,
                        isRetryCall: false
                    });
                }
                const payload = {
                    conversationId: isRetryCall ? this.outgoingCallInfo.convId : this.activeConversation.id,
                    conversationRoomType: roomType
                };
                const startRoomResponse = await this._messageService.startConversationRoom(payload);
                await this.sendCallNotification(startRoomResponse.roomId, isRetryCall);
                this._analyticsService.logEvent({
                    category: AnalyticsCategory.AxxessConnect,
                    action: "Connect Audio/Video call"
                });
            }
        },

        async handleJoinRoom(roomId: string, isRetryCall: boolean) {
            if (isRetryCall) {
                await jitsiRoomHandler(this, roomId, this.showCallHeader.callStartTime, this.callControls, {
                    users: this.outgoingCallInfo.users,
                    conversationRoomType: this.outgoingCallInfo.roomType
                });
            } else
                await jitsiRoomHandler(this, roomId, this.showCallHeader.callStartTime, this.callControls, {
                    users: this.activeConversation.loginIds,
                    conversationRoomType: this.activeConversationType
                });
        },

        addNewMemberDialog() {
            this.dialogRef = this.$dialog.open(AddMemberToChat, {
                props: {
                    modal: true,
                    showHeader: false,
                    contentClass: "d-flex flex-column justify-content-between",
                    style: {
                        width: "400px",
                        height: "464px"
                    }
                },
                data: {
                    conversationId: this.activeConversation.id,
                    loginIds: this.activeConversation.loginIds,
                    isGroupConversation: this.isGroupConversation,
                    isDraftConversation: this.activeConversation.isDraftConversation
                },
                onClose: (options: any) => {
                    if (!options.data?.status) return;
                }
            });
        },

        async leaveGroup() {
            const promptOptions: IPromptOptions = {
                message: "Are you sure you want to leave the group?",
                okText: "Leave",
                cancelText: "Cancel"
            };

            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },

                data: {
                    promptOptions
                },

                onClose: async (options: any) => {
                    if (options.data === "success") {
                        await this._messageService.leaveGroup(this.activeConversation.id);
                        this.updateConversationUsers(
                            this.activeConversation.id,
                            "leave",
                            this.loggedInUserDetails.userId
                        );
                        await this.fetchNewerMessages(this.activeConversation.id, true);
                    }
                }
            });
        },

        async removeParticipant(user: IConversationUser) {
            const promptOptions: IPromptOptions = {
                message: `Are you sure you want to
                remove ${user.firstName} ${user.lastName} from the group?`,
                okText: "Remove",
                cancelText: "Cancel"
            };

            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },

                data: {
                    promptOptions
                },

                onClose: async (options) => {
                    if (options.data !== "success") {
                        return;
                    }
                    try {
                        if (this.activeConversation.isDraftConversation) {
                            const draftDetails = {
                                id: this.activeConversation.id,
                                participantId: user.loginId,
                                actionType: DraftConvEnum.RemoveParticipant
                            };
                            this.updateDraftConversation(draftDetails);
                        } else await this._messageService.removeMember(this.activeConversation.id, user.userId);
                    } catch (error) {
                        console.error(error);
                    }
                }
            });
        },

        handleCallControlsUpdate(callControls: ICallControls) {
            this.callControls = callControls;
        },
        handleRetryCall() {
            if (this.$route?.query?.tab === ChatNavOptionsEnum.Chat) {
                this.startMeeting(this.outgoingCallInfo.roomType, true);
            }
        }
    },

    beforeUnmount() {
        clearInterval(this.intervalId);
        eventBus.off("dialogOpened", this.handleJitsiDialogOpened);
        eventBus.off("updateCallControls", this.handleCallControlsUpdate);
        eventBus.off("retryCall", this.handleRetryCall);
        eventBus.off("startJitiCall", this.handleStartJitsiCall);
    }
});
